import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { toast } from "sonner";
import { ChevronDown, FileText, Heart } from "lucide-react";
import { AppShell, PageLoader } from "../components/AppShell";
import { CopyBtn } from "../components/CopyBtn";
import { ShareDropdown } from "../components/ShareDropdown";
import { MdOut } from "../components/MdOut";
import { api } from "../lib/api";
import { tplIcons } from "../components/icons";

export default function FavoritesPage() {
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/generations/favorites");
        setFavs(data);
      } catch (err) {
        logger.error("Failed to load favorites", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const unfav = async (id) => {
    try {
      await api.post(`/generations/${id}/favorite`);
      setFavs((prev) => prev.filter((g) => g.id !== id));
      toast.success("Removed");
    } catch (err) {
      logger.error("Unfavorite failed", err);
      toast.error("Failed to update");
    }
  };

  if (loading) return <PageLoader />;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="favorites-page">
        <h1 className="text-3xl font-black tracking-tighter text-zinc-950 mb-8 font-heading">Favorites</h1>
        {favs.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-favorites">
            <Heart className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No favorites yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {favs.map((g) => {
              const Icon = tplIcons[g.template] || FileText;
              const isE = expanded === g.id;
              return (
                <div key={g.id} className="bg-white border border-zinc-200" data-testid={`favorite-item-${g.id}`}>
                  <div className="flex items-center gap-4 p-4">
                    <div className="w-10 h-10 bg-brand/5 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-brand" />
                    </div>
                    <button onClick={() => setExpanded(isE ? null : g.id)} className="flex-1 min-w-0 text-left">
                      <p className="font-semibold text-zinc-900 text-sm truncate">{g.topic}</p>
                      <p className="text-xs text-zinc-500">{g.template_name} &middot; {g.word_count || 0} words</p>
                    </button>
                    <button onClick={() => unfav(g.id)} className="p-1.5 hover:bg-red-50" data-testid={`unfav-btn-${g.id}`}>
                      <Heart className="w-4 h-4 fill-red-500 text-red-500" />
                    </button>
                    <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform cursor-pointer ${isE ? "rotate-180" : ""}`} onClick={() => setExpanded(isE ? null : g.id)} />
                  </div>
                  {isE && (
                    <div className="border-t border-zinc-100 p-5">
                      <div className="flex justify-end mb-3 gap-2">
                        <CopyBtn text={g.output} label="Copy" />
                        <ShareDropdown text={g.output} title={g.topic} />
                      </div>
                      <MdOut content={g.output} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
